import React, { useState } from 'react';

interface ResultsVisualizationProps {
  analysisResults?: any;
  onVisualizationChange?: (config: any) => void;
}

export const ResultsVisualization: React.FC<ResultsVisualizationProps> = ({ 
  analysisResults, 
  onVisualizationChange 
}) => {
  const [visualizationType, setVisualizationType] = useState<'contour' | 'deformed' | 'vector' | 'animation'>('contour');
  const [resultType, setResultType] = useState<'displacement' | 'stress' | 'force' | 'moment'>('displacement');
  const [scale, setScale] = useState(1.0);
  const [showUndeformed, setShowUndeformed] = useState(true);
  const [colorScheme, setColorScheme] = useState<'rainbow' | 'thermal' | 'grayscale'>('rainbow');

  const handleVisualizationUpdate = () => {
    const config = {
      type: visualizationType,
      resultType,
      scale,
      showUndeformed,
      colorScheme,
      timestamp: Date.now()
    };
    onVisualizationChange?.(config);
  };

  return (
    <div className="results-visualization bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
        Results Visualization
      </h3>
      
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Visualization Type
          </label>
          <select
            value={visualizationType}
            onChange={(e) => setVisualizationType(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="contour">Contour Plot</option>
            <option value="deformed">Deformed Shape</option>
            <option value="vector">Vector Plot</option>
            <option value="animation">Animation</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Result Type
          </label>
          <select
            value={resultType}
            onChange={(e) => setResultType(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="displacement">Displacement</option>
            <option value="stress">Stress</option>
            <option value="force">Force</option>
            <option value="moment">Moment</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Scale Factor
          </label>
          <input
            type="number"
            value={scale}
            onChange={(e) => setScale(parseFloat(e.target.value) || 1.0)}
            step="0.1"
            min="0.1"
            max="10"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Color Scheme
          </label>
          <select
            value={colorScheme}
            onChange={(e) => setColorScheme(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="rainbow">Rainbow</option>
            <option value="thermal">Thermal</option>
            <option value="grayscale">Grayscale</option>
          </select>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={showUndeformed}
              onChange={(e) => setShowUndeformed(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Show Undeformed Shape
            </span>
          </label>
        </div>
      </div>

      {/* Visualization Preview */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
          Visualization Preview
        </h4>
        <div className="bg-white dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg h-48 flex items-center justify-center">
          <div className="text-center text-gray-500 dark:text-gray-400">
            <div className="text-4xl mb-2">📊</div>
            <div className="text-sm">
              {visualizationType.charAt(0).toUpperCase() + visualizationType.slice(1)} visualization
            </div>
            <div className="text-xs">
              {resultType.charAt(0).toUpperCase() + resultType.slice(1)} results
            </div>
          </div>
        </div>
      </div>

      {/* Color Legend */}
      <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
          Color Legend
        </h4>
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-600 dark:text-gray-400">Min</div>
          <div className="flex-1 mx-3 h-4 bg-gradient-to-r from-blue-500 to-red-500 rounded"></div>
          <div className="text-xs text-gray-600 dark:text-gray-400">Max</div>
        </div>
        <div className="flex justify-between mt-1 text-xs text-gray-600 dark:text-gray-400">
          <span>0.0</span>
          <span>2.45 mm</span>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-6 flex justify-between items-center">
        <div className="flex space-x-3">
          <button className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Reset View
          </button>
          <button className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Screenshot
          </button>
        </div>
        
        <button
          onClick={handleVisualizationUpdate}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
        >
          Update Visualization
        </button>
      </div>
    </div>
  );
};
